
import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Zap, CheckCircle, AlertCircle, Code, Bot, User, Send, Sparkles } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface AIProgramCreatorProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

interface GeneratedProgram {
  name: string;
  code: string;
  legalCompanyName: string;
  programType: string;
  loyaltyPointName: string;
  earnCostPerPoint: string;
  burnCostPerPoint: string;
  defaultCurrency: string;
  location: string;
  city: string;
  country: string;
  websiteUrl: string;
  vat: string;
  confidence: number;
  description: string;
  sector: string;
  expectedROI: string;
}

export function AIProgramCreator({ isOpen, onClose }: AIProgramCreatorProps) {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'ai',
      content: "Hello! I'm your AI assistant for program creation. I can help you create loyalty programs, design reward structures, set up member tiers, and much more. What would you like to create today?",
      timestamp: new Date()
    }
  ]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedProgram, setGeneratedProgram] = useState<GeneratedProgram | null>(null);
  const [conversationData, setConversationData] = useState<any>({});
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Enhanced suggestions from AI-chat page
  const suggestions = [
    "Create me a 3-tier program where silver holders earn 1 point per $5, and gold holders earn 1.5 points per $5, with points expiring in 12 months",
    "Design a cashback program with 2% rewards for groceries and 1% for everything else",
    "Set up a referral program that gives 500 bonus points for each successful referral",
    "Create a birthday rewards program that gives members a special discount on their birthday month",
    "Design a VIP program with exclusive access to premium rewards and early sale notifications",
    "Build a seasonal promotion that doubles points during holiday shopping periods",
    "Create a milestone program that rewards members for reaching spending thresholds",
    "Create a premium coffee shop loyalty program with points for purchases",
    "Design a fitness gym membership program with tier-based rewards",
    "Build a retail chain program with cashback and exclusive offers",
    "Set up an airline miles program with partner merchant integration",
    "Create a restaurant loyalty program with birthday bonuses",
    "Design a hotel rewards program with room upgrades and free nights"
  ];

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

  const generateAIResponse = (userInput: string, currentData: any): { response: string, nextStep: string | null } => {
    const input = userInput.toLowerCase();
    
    // Initial program description
    if (!currentData.description) {
      return {
        response: `Great! I understand you want to create: "${userInput}"

Let me gather some key details to create the perfect program for you:

**What should be the name of your loyalty program?**
For example: "Premium Rewards", "Coffee Club", "VIP Points", etc.`,
        nextStep: 'name'
      };
    }

    // Program name
    if (currentData.description && !currentData.name) {
      return {
        response: `Perfect! "${userInput}" is a great name for your program.

**What symbol or short code would you like for your loyalty points?**
For example: "Points", "Stars", "Miles", "Coins", "Beans", etc.`,
        nextStep: 'symbol'
      };
    }

    // Symbol/Point name
    if (currentData.name && !currentData.symbol) {
      return {
        response: `Excellent! Your loyalty points will be called "${userInput}".

**Which sector or industry is this program for?**
For example: "Retail", "Hospitality", "Food & Beverage", "Fitness", "E-commerce", "Travel", etc.`,
        nextStep: 'sector'
      };
    }

    // Sector
    if (currentData.symbol && !currentData.sector) {
      return {
        response: `Great choice! The ${currentData.symbol} program for the ${userInput} sector.

**What's your expected ROI (Return on Investment) for this program?**
For example: "15%", "20-25%", "Increase customer retention by 30%", etc.`,
        nextStep: 'roi'
      };
    }

    // ROI and final program creation
    if (currentData.sector && !currentData.roi) {
      return {
        response: `Perfect! Expected ROI of ${userInput} is a solid target.

**What's your target geographic location for this program?**
For example: "Dubai, UAE", "New York, USA", "London, UK", etc.`,
        nextStep: 'location'
      };
    }

    // Location and final program creation
    if (currentData.roi && !currentData.location) {
      return {
        response: `Excellent! I now have all the information needed to create your ${currentData.name} program.

**Program Summary:**
• **Name**: ${currentData.name}
• **Points**: ${currentData.symbol}
• **Sector**: ${currentData.sector}
• **Expected ROI**: ${currentData.roi}
• **Location**: ${userInput}
• **Description**: ${currentData.description}

Should I proceed to create this program with the recommended settings based on your requirements?`,
        nextStep: 'create'
      };
    }

    return {
      response: "I understand. Let me create your program now with all the details you've provided.",
      nextStep: 'create'
    };
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = message;
    setMessage('');
    setIsGenerating(true);

    try {
      // Simulate AI processing
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const { response, nextStep } = generateAIResponse(currentMessage, conversationData);
      
      // Update conversation data based on the step
      let newData = { ...conversationData };
      if (!newData.description) {
        newData.description = currentMessage;
      } else if (!newData.name) {
        newData.name = currentMessage;
      } else if (!newData.symbol) {
        newData.symbol = currentMessage;
      } else if (!newData.sector) {
        newData.sector = currentMessage;
      } else if (!newData.roi) {
        newData.roi = currentMessage;
      } else if (!newData.location) {
        newData.location = currentMessage;
      }
      
      setConversationData(newData);
      
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
      
      // If ready to create, generate the program
      if (nextStep === 'create' && newData.location) {
        setTimeout(() => {
          createFinalProgram(newData);
        }, 1000);
      }
      
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const createFinalProgram = (data: any) => {
    const locationParts = data.location.split(',').map((part: string) => part.trim());
    const city = locationParts[0] || "Dubai";
    const country = locationParts[1] || "United Arab Emirates";
    
    const mockProgram: GeneratedProgram = {
      name: data.name,
      code: `${data.name.substring(0, 4).toUpperCase()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
      legalCompanyName: `${data.name} Corp`,
      programType: "earnburn",
      loyaltyPointName: data.symbol,
      earnCostPerPoint: "0.01",
      burnCostPerPoint: "0.015",
      defaultCurrency: "USD",
      location: "Business District",
      city: city,
      country: country,
      websiteUrl: "https://example.com/rewards",
      vat: "5",
      confidence: Math.floor(Math.random() * 15) + 85,
      description: data.description,
      sector: data.sector,
      expectedROI: data.roi
    };
    
    setGeneratedProgram(mockProgram);
  };

  const handleSuggestedPrompt = (prompt: string) => {
    setMessage(prompt);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleApprove = () => {
    if (generatedProgram) {
      console.log("Approved program:", generatedProgram);
      onClose();
    }
  };

  const handleRefine = () => {
    setGeneratedProgram(null);
    setConversationData({});
    setMessages([
      {
        id: '1',
        type: 'ai',
        content: "Let's refine your program. What would you like to change or improve?",
        timestamp: new Date()
      }
    ]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-purple-500" />
            AI Program Creator
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {!generatedProgram ? (
            <>
              {/* Suggested Prompts Sidebar */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Sparkles className="h-4 w-4 text-yellow-500" />
                    Quick Start Ideas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    <div className="space-y-2">
                      {suggestions.map((suggestion, index) => (
                        <Button 
                          key={index}
                          variant="outline" 
                          className="w-full text-left h-auto py-2 px-2 text-xs whitespace-normal"
                          onClick={() => handleSuggestedPrompt(suggestion)}
                        >
                          <div className="flex items-start gap-1 w-full">
                            <Zap className="h-3 w-3 mt-0.5 text-blue-500 flex-shrink-0" />
                            <span className="text-left text-xs">{suggestion}</span>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
              
              {/* Chat Interface */}
              <Card className="lg:col-span-3 flex flex-col h-[500px]">
                <CardHeader className="flex-shrink-0">
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-blue-500" />
                    AI Assistant Chat
                    <Badge variant="secondary" className="ml-auto">
                      Program Creator
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col p-0 min-h-0">
                  {/* Messages Area */}
                  <ScrollArea className="flex-1 p-6 min-h-0" ref={scrollAreaRef}>
                    <div className="space-y-4 pr-4">
                      {messages.map((msg) => (
                        <div 
                          key={msg.id} 
                          className={`flex gap-3 ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          {msg.type === 'ai' && (
                            <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                              <Bot className="h-4 w-4 text-white" />
                            </div>
                          )}
                          
                          <div className={`max-w-[80%] ${msg.type === 'user' ? 'order-first' : ''}`}>
                            <div 
                              className={`rounded-lg px-4 py-3 ${
                                msg.type === 'user' 
                                  ? 'bg-blue-500 text-white ml-auto' 
                                  : 'bg-gray-100 text-gray-900'
                              }`}
                            >
                              <div className="whitespace-pre-wrap text-sm leading-relaxed">
                                {msg.content}
                              </div>
                            </div>
                            <div className={`text-xs text-gray-500 mt-1 ${msg.type === 'user' ? 'text-right' : 'text-left'}`}>
                              {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                          
                          {msg.type === 'user' && (
                            <div className="h-8 w-8 rounded-full bg-gray-500 flex items-center justify-center flex-shrink-0">
                              <User className="h-4 w-4 text-white" />
                            </div>
                          )}
                        </div>
                      ))}
                      
                      {isGenerating && (
                        <div className="flex gap-3 justify-start">
                          <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center">
                            <Bot className="h-4 w-4 text-white" />
                          </div>
                          <div className="bg-gray-100 rounded-lg px-4 py-3">
                            <div className="flex space-x-1">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                  
                  {/* Message Input */}
                  <div className="border-t p-4 flex-shrink-0">
                    <div className="flex gap-3">
                      <Textarea 
                        placeholder="Describe the program you want to create..." 
                        className="flex-1 min-h-[60px] max-h-[120px] resize-none"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isGenerating}
                      />
                      <Button 
                        onClick={handleSendMessage}
                        disabled={isGenerating || !message.trim()}
                        className="px-6"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <span>Press Enter to send, Shift+Enter for new line</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            // Generated Program Display
            <Card className="lg:col-span-4">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    Generated Program
                  </span>
                  <Badge variant="secondary" className="bg-green-100 text-green-800">
                    {generatedProgram.confidence}% Confidence
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-2">Program Overview</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="text-sm bg-gray-50 p-3 rounded">
                      <span className="font-medium">Name:</span> {generatedProgram.name}
                    </div>
                    <div className="text-sm bg-gray-50 p-3 rounded">
                      <span className="font-medium">Code:</span> {generatedProgram.code}
                    </div>
                    <div className="text-sm bg-gray-50 p-3 rounded">
                      <span className="font-medium">Sector:</span> {generatedProgram.sector}
                    </div>
                    <div className="text-sm bg-gray-50 p-3 rounded">
                      <span className="font-medium">Expected ROI:</span> {generatedProgram.expectedROI}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-2">Loyalty Details</h4>
                    <div className="space-y-2">
                      <div className="text-sm bg-blue-50 p-2 rounded">
                        <span className="font-medium">Point Name:</span> {generatedProgram.loyaltyPointName}
                      </div>
                      <div className="text-sm bg-blue-50 p-2 rounded">
                        <span className="font-medium">Earn Cost:</span> {generatedProgram.defaultCurrency} {generatedProgram.earnCostPerPoint}
                      </div>
                      <div className="text-sm bg-blue-50 p-2 rounded">
                        <span className="font-medium">Burn Cost:</span> {generatedProgram.defaultCurrency} {generatedProgram.burnCostPerPoint}
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">Location & Settings</h4>
                    <div className="space-y-2">
                      <div className="text-sm bg-green-50 p-2 rounded">
                        <span className="font-medium">Location:</span> {generatedProgram.city}, {generatedProgram.country}
                      </div>
                      <div className="text-sm bg-green-50 p-2 rounded">
                        <span className="font-medium">Currency:</span> {generatedProgram.defaultCurrency}
                      </div>
                      <div className="text-sm bg-green-50 p-2 rounded">
                        <span className="font-medium">VAT:</span> {generatedProgram.vat}%
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-2">Description</h4>
                  <p className="text-sm bg-gray-50 p-2 rounded">{generatedProgram.description}</p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-between pt-4 border-t">
                  <Button variant="outline" onClick={handleRefine}>
                    Refine Program
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button onClick={handleApprove} className="bg-green-600 hover:bg-green-700">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Create Program
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
